import { useLayoutEffect, useRef, useState, type RefObject } from 'react';

/**
 * Reads flex `column-gap` from a measure row. Returns `0` when missing or invalid.
 */
function parseGap(measureContainer: HTMLElement): number {
  const gap = getComputedStyle(measureContainer).columnGap;
  const parsed = parseFloat(gap);
  return Number.isFinite(parsed) ? parsed : 0;
}

/** The container's width for items: without its padding (room it keeps for focus rings). */
function contentWidth(container: HTMLElement): number {
  const style = getComputedStyle(container);
  const padding =
    (parseFloat(style.paddingLeft) || 0) +
    (parseFloat(style.paddingRight) || 0);
  return container.offsetWidth - padding;
}

function measureIndicesWidth(
  indices: number[],
  children: HTMLElement[],
  gap: number
): number {
  return indices.reduce((total, index, position) => {
    const child = children[index];
    if (!child) return total;
    return total + (position > 0 ? gap : 0) + child.offsetWidth;
  }, 0);
}

function computeMaxVisibleCount(
  children: HTMLElement[],
  containerWidth: number,
  gap: number,
  buttonWidth: number
): number {
  if (children.length === 0) return 0;

  const allChildrenWidth = measureIndicesWidth(
    children.map((_, index) => index),
    children,
    gap
  );
  if (allChildrenWidth <= containerWidth) {
    return children.length;
  }

  let visibleCount = 0;
  let totalWidth = 0;

  for (let index = 0; index < children.length; index++) {
    const widthWithChild =
      totalWidth + (visibleCount > 0 ? gap : 0) + children[index].offsetWidth;
    const needsOverflowButton = index < children.length - 1;
    const wouldExceed = needsOverflowButton
      ? widthWithChild + gap + buttonWidth > containerWidth
      : widthWithChild > containerWidth;

    if (wouldExceed) break;

    totalWidth = widthWithChild;
    visibleCount++;
  }

  return Math.max(visibleCount, 1);
}

function fitsVisibleIndices(
  visibleIndices: number[],
  children: HTMLElement[],
  containerWidth: number,
  gap: number,
  buttonWidth: number,
  itemCount: number
): boolean {
  const visibleWidth = measureIndicesWidth(visibleIndices, children, gap);
  const hasOverflow = visibleIndices.length < itemCount;
  const maxAllowed = hasOverflow
    ? containerWidth - gap - buttonWidth
    : containerWidth;
  return visibleWidth <= maxAllowed;
}

/**
 * Layout snapshot produced by overflow measurement.
 * `pinnedOutOfOrderIndex` is set when the selected item is kept visible outside
 * its natural position in the list.
 */
interface FlexOverflowLayout {
  visibleIndices: number[];
  overflowIndices: number[];
  pinnedOutOfOrderIndex?: number;
}

function computeVisibleIndices(
  children: HTMLElement[],
  containerWidth: number,
  gap: number,
  buttonWidth: number,
  pinnedIndex?: number
): FlexOverflowLayout {
  const itemCount = children.length;
  if (itemCount === 0) {
    return { visibleIndices: [], overflowIndices: [] };
  }

  const maxVisibleCount = computeMaxVisibleCount(
    children,
    containerWidth,
    gap,
    buttonWidth
  );

  const buildResult = (
    visibleIndices: number[],
    pinnedOutOfOrderIndex?: number
  ): FlexOverflowLayout => {
    const visibleSet = new Set(visibleIndices);
    const overflowIndices = Array.from(
      { length: itemCount },
      (_, index) => index
    ).filter(index => !visibleSet.has(index));
    return { visibleIndices, overflowIndices, pinnedOutOfOrderIndex };
  };

  const hasPinnedIndex =
    pinnedIndex !== undefined && pinnedIndex >= 0 && pinnedIndex < itemCount;

  if (!hasPinnedIndex || pinnedIndex < maxVisibleCount) {
    const visibleIndices = Array.from(
      { length: maxVisibleCount },
      (_, index) => index
    );
    return buildResult(visibleIndices);
  }

  let visibleCount = maxVisibleCount;
  while (visibleCount >= 2) {
    const visibleIndices = [
      ...Array.from({ length: visibleCount - 1 }, (_, index) => index),
      pinnedIndex
    ];
    if (
      fitsVisibleIndices(
        visibleIndices,
        children,
        containerWidth,
        gap,
        buttonWidth,
        itemCount
      )
    ) {
      return buildResult(visibleIndices, pinnedIndex);
    }
    visibleCount--;
  }

  return buildResult([pinnedIndex], pinnedIndex);
}

function isSameLayout(a: FlexOverflowLayout, b: FlexOverflowLayout): boolean {
  return (
    a.pinnedOutOfOrderIndex === b.pinnedOutOfOrderIndex &&
    a.visibleIndices.length === b.visibleIndices.length &&
    a.visibleIndices.every(
      (index, position) => index === b.visibleIndices[position]
    ) &&
    a.overflowIndices.length === b.overflowIndices.length &&
    a.overflowIndices.every(
      (index, position) => index === b.overflowIndices[position]
    )
  );
}

function sanitizeLayout(
  layout: FlexOverflowLayout,
  itemCount: number
): FlexOverflowLayout {
  if (itemCount === 0) {
    return { visibleIndices: [], overflowIndices: [] };
  }

  const isValidIndex = (index: number) => index >= 0 && index < itemCount;

  const visibleIndices = layout.visibleIndices.filter(isValidIndex);
  const overflowIndices = layout.overflowIndices.filter(isValidIndex);
  const pinnedOutOfOrderIndex =
    layout.pinnedOutOfOrderIndex !== undefined &&
    isValidIndex(layout.pinnedOutOfOrderIndex)
      ? layout.pinnedOutOfOrderIndex
      : undefined;

  if (visibleIndices.length > 0) {
    return { visibleIndices, overflowIndices, pinnedOutOfOrderIndex };
  }

  return {
    visibleIndices: Array.from({ length: itemCount }, (_, index) => index),
    overflowIndices: []
  };
}

export interface UseFlexOverflowOptions {
  /**
   * Number of items in the measure row. A child after them is the overflow
   * trigger, measured for the room it takes when shown.
   */
  itemCount: number;
  /**
   * Index of the currently selected item. When it would overflow, it is kept
   * visible (possibly out of order) instead of moving into the overflow popover.
   */
  pinnedIndex?: number;
}

/**
 * Refs and derived indices returned by {@link useFlexOverflow}.
 *
 * Attach `containerRef` to the width-constrained wrapper and `measureRef` to a
 * hidden row that renders every child for measurement.
 */
export interface UseFlexOverflowResult {
  /** Width-constrained container observed for available horizontal space. */
  containerRef: RefObject<HTMLDivElement>;
  /** Hidden row containing all children used for width measurement. */
  measureRef: RefObject<HTMLDivElement>;
  /** Indices of children shown in the visible row. */
  visibleIndices: number[];
  /** Indices of children placed in the overflow menu. */
  overflowIndices: number[];
  /** Index of the selected item when it is shown outside its natural order. */
  pinnedOutOfOrderIndex?: number;
}

/**
 * Measures a horizontal flex row and decides which child indices stay visible
 * versus overflow, reserving space for an overflow trigger button.
 *
 * Recalculates synchronously in `useLayoutEffect` when `itemCount` or
 * `pinnedIndex` change (avoids a stale frame on selection changes). Container
 * resizes are observed via `ResizeObserver` and recalculated on the next frame.
 *
 * @example
 * ```tsx
 * const { containerRef, measureRef, visibleIndices, overflowIndices } =
 *   useFlexOverflow({ itemCount: tabs.length, pinnedIndex: selectedIndex });
 * ```
 */
export function useFlexOverflow({
  itemCount,
  pinnedIndex
}: UseFlexOverflowOptions): UseFlexOverflowResult {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [layout, setLayout] = useState<FlexOverflowLayout>({
    visibleIndices: Array.from({ length: itemCount }, (_, index) => index),
    overflowIndices: []
  });

  useLayoutEffect(() => {
    setLayout(previous => {
      const sanitized = sanitizeLayout(previous, itemCount);
      return isSameLayout(previous, sanitized) ? previous : sanitized;
    });

    const calculateOverflow = () => {
      const container = containerRef.current;
      const measureContainer = measureRef.current;
      if (!container || !measureContainer) return;

      const measured = Array.from(measureContainer.children) as HTMLElement[];
      if (measured.length !== itemCount && measured.length !== itemCount + 1) {
        return;
      }
      const children = measured.slice(0, itemCount);
      const triggerWidth = measured[itemCount]?.offsetWidth ?? 0;

      const gap = parseGap(measureContainer);

      const nextLayout = computeVisibleIndices(
        children,
        contentWidth(container),
        gap,
        triggerWidth,
        pinnedIndex
      );

      setLayout(previous =>
        isSameLayout(previous, nextLayout) ? previous : nextLayout
      );
    };

    // Run synchronously before paint so a `pinnedIndex` change does not show a
    // stale frame (old visible arrangement) before the layout is recalculated.
    calculateOverflow();

    // Resize callbacks are deferred to the next frame to avoid
    // "ResizeObserver loop" warnings.
    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(calculateOverflow);
    });
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    if (measureRef.current) {
      resizeObserver.observe(measureRef.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, [itemCount, pinnedIndex]);

  const safeLayout = sanitizeLayout(layout, itemCount);

  return {
    containerRef,
    measureRef,
    visibleIndices: safeLayout.visibleIndices,
    overflowIndices: safeLayout.overflowIndices,
    pinnedOutOfOrderIndex: safeLayout.pinnedOutOfOrderIndex
  };
}
